import React, { useState, useRef, useEffect } from 'react';
import { Save, Upload } from "lucide-react";
import constant from "../../../constant";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const IMSTable = ({ ims, onStatusChange, currentMonthFilter, onMonthYearFilterChange }) => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionUpdates, setActionUpdates] = useState({});
  const [selectedIms, setSelectedIms] = useState([]);
  const [notification, setNotification] = useState(null);
  const [loadingImsIds, setLoadingImsIds] = useState([]);
  const [localImsData, setLocalImsData] = useState(ims);

  const userRole = localStorage.getItem("role");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedUploadMonth, setSelectedUploadMonth] = useState('');
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setLocalImsData(ims);
    setActionUpdates({});
    // setSelectedIms([]);
  }, [ims]);

  const handleActionChange = (id, newAction) => {
    setActionUpdates(prevUpdates => ({
      ...prevUpdates,
      [id]: newAction
    }));
  };

  const handleSaveStatus = async (imsId) => {
    const newStatus = actionUpdates[imsId];
    if (!newStatus) {
      setNotification({ type: 'error', message: "Please select a status before saving." });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    setLoadingImsIds(prev => [...prev, imsId]);

    const originalStatus = localImsData.find(item => item._id === imsId)?.status;
    setLocalImsData(prevData =>
      prevData.map(item =>
        item._id === imsId ? { ...item, status: newStatus } : item
      )
    );

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setNotification({ type: 'error', message: "No authentication token found." });
        setTimeout(() => setNotification(null), 3000);
        if (originalStatus) setLocalImsData(prevData => prevData.map(item => item._id === imsId ? { ...item, status: originalStatus } : item));
        setLoadingImsIds(prev => prev.filter(id => id !== imsId));
        return;
      }

      const response = await fetch(
        `${constant.REACT_APP_API_URL}/purchase/ims/${imsId}`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (response.ok) {
        if (onStatusChange) {
          onStatusChange();
        }
        setActionUpdates(prevUpdates => {
          const { [imsId]: _, ...rest } = prevUpdates;
          return rest;
        });
        setNotification({ type: 'success', message: "Status updated successfully!" });
        setTimeout(() => setNotification(null), 3000);
      } else {
        if (originalStatus) setLocalImsData(prevData => prevData.map(item => item._id === imsId ? { ...item, status: originalStatus } : item));
        const result = await response.json();
        setNotification({ type: 'error', message: result.message || "Failed to update IMS status." });
        setTimeout(() => setNotification(null), 3000);
      }
    } catch (error) {
      if (originalStatus) setLocalImsData(prevData => prevData.map(item => item._id === imsId ? { ...item, status: originalStatus } : item));
      console.error(error);
      setNotification({ type: 'error', message: error.message || "An error occurred while updating status." });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setLoadingImsIds(prev => prev.filter(id => id !== imsId));
    }
  };

  const filteredIms = localImsData.filter((imsItem) => {
    const currentItemStatus = actionUpdates[imsItem._id] || imsItem.status || 'pending';
    const matchesStatus = statusFilter === "all" ? true : currentItemStatus === statusFilter;
    return matchesStatus;
  });


  const openUploadModal = () => {
    setSelectedUploadMonth('');
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setShowUploadModal(true);
  };

  const handleUpload = async (file, imsMonth) => {
    const userId = localStorage.getItem("adminSelectedUser");
    const token = localStorage.getItem("token");

    if (!token || (userRole === 'admin' && !userId)) {
      setNotification({ type: 'error', message: 'Authentication details missing or user not selected for admin. Cannot upload.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    setIsUploading(true);
    setNotification(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('imsMonth', imsMonth);
    let apiUrl = `${constant.REACT_APP_API_URL}/user/admin-uploads/`;
    if (userRole === 'admin' && userId) {
      apiUrl += `?userId=${userId}`;
    }


    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // 'Content-Type': 'multipart/form-data'
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setNotification({ type: 'success', message: result.message || 'File uploaded successfully!' });
        setTimeout(() => setNotification(null), 5000);
        if (onStatusChange) {
          onStatusChange();
        }
      } else {
        let errorMessage = `Upload failed: ${response.statusText}`;
        try {
          const errorResult = await response.json();
          errorMessage = errorResult.message || errorMessage;
        } catch (e) { /* ignore */ }
        console.error("Upload failed:", response.status, errorMessage);
        setNotification({ type: 'error', message: errorMessage });
        setTimeout(() => setNotification(null), 5000);
      }
    } catch (error) {
      console.error("Upload error:", error);
      setNotification({ type: 'error', message: `An network error occurred: ${error.message}` });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsUploading(false);
      setShowUploadModal(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleConfirmAndUpload = () => {
    const file = fileInputRef.current?.files?.[0];

    if (!selectedUploadMonth) {
      setNotification({ type: 'error', message: 'Please select a month and year for the upload.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    if (!file) {
      setNotification({ type: 'error', message: 'No file selected.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    const dateObj = new Date(selectedUploadMonth);
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const formattedMonthYear = `${year}-${month}`;

    // console.log(formattedMonthYear)

    handleUpload(file, formattedMonthYear);
  };


  return (
    <div className="max-w-8xl mx-auto px-0 lg:px-8 py-8">
      {notification && (
        <div className={`fixed top-4 right-4 z-[150] p-4 rounded shadow-lg ${notification.type === 'success' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
          {notification.message}
        </div>
      )}

      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full flex justify-center items-center z-[100]">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md mx-4 sm:mx-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Upload IMS File</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="upload-month" className="block text-sm font-medium text-gray-700">
                  Select Month and Year for Upload <span className="text-red-500">*</span>
                </label>
                {/* Using type="month" for better UX for selecting month/year */}
                <input
                  type="date"
                  id="upload-month"
                  name="upload-month"
                  value={selectedUploadMonth} // Expects YYYY-MM format
                  onChange={(e) => setSelectedUploadMonth(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="file-upload-input-modal" className="block text-sm font-medium text-gray-700">
                  Select File <span className="text-red-500">*</span>
                </label>
                <input
                  id="file-upload-input-modal"
                  type="file"
                  ref={fileInputRef}
                  className="mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none p-2"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAndUpload}
                className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </>
                ) : 'Upload File'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">IMS Purchases</h1>
            <p className="mt-1 text-xs text-gray-500">
              Displaying {filteredIms.length} of {localImsData.length} records
              {currentMonthFilter ? ` for ${currentMonthFilter.toLocaleString('default', { month: 'long', year: 'numeric' })}` : ''}
            </p>
          </div>
          <div className="flex gap-3">
            {userRole === 'admin' && (
              <button
                onClick={openUploadModal}
                className={`inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors duration-150 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isUploading}
              >
                <Upload size={18} className="mr-2" />
                Upload
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
          <div className="relative w-full sm:w-96">
            <DatePicker
              selected={currentMonthFilter}
              onChange={onMonthYearFilterChange}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              showFullMonthYearPicker
              placeholderText="Filter by Month and Year"
              className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
              wrapperClassName="w-full"
              popperPlacement="bottom-start"
              isClearable={!!currentMonthFilter}
            />
          </div>
          <div className="flex gap-4 items-center bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex gap-2">
              {["all", "pending", "approved", "rejected"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-md text-xs font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400 ${statusFilter === status ? "bg-blue-600 text-white shadow-md" : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-100 hover:shadow-sm"}`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <div className="min-w-full overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '120px' }}>Status</th>
                {userRole !== "admin" && (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '80px' }}>Actions</th>
                )}
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GSTIN</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trade Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Number</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Value</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Place of Supply</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taxable Value</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Integrated Tax</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Central Tax</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State Tax</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cess</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source Return Period</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source Filling Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredIms.length === 0 ? (
                <tr>
                  <td colSpan={userRole === "admin" ? 17 : 18} className="px-4 py-10 text-center text-sm text-gray-500">
                    No IMS records match the current filters.
                  </td>
                </tr>
              ) : (
                filteredIms.map((imsItem) => (
                  <tr key={imsItem._id} className={`hover:bg-gray-50 transition-colors duration-150`}>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <select
                        value={actionUpdates[imsItem._id] || imsItem.status || 'No Action'}
                        onChange={(e) => handleActionChange(imsItem._id, e.target.value)}
                        className="shadow-sm focus:ring-indigo-500 p-1 focus:border-indigo-500 block w-full sm:text-xs border-gray-300 rounded-md"
                        style={{ minWidth: '100px' }}
                        disabled={userRole === "admin"}
                      >
                        <option value="No Action">No Action</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                    {userRole !== "admin" && (
                      <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleSaveStatus(imsItem._id)}
                            className={`p-2 rounded-md transition-colors duration-150 flex items-center justify-center ${loadingImsIds.includes(imsItem._id) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-700 text-white'} ${!actionUpdates[imsItem._id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={loadingImsIds.includes(imsItem._id) || !actionUpdates[imsItem._id]}
                            title={!actionUpdates[imsItem._id] ? "Select a status first" : "Save Status"}
                          >
                            {loadingImsIds.includes(imsItem._id) ? (
                              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <Save size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-800 font-medium">{imsItem.gstinOfSupplier || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">{imsItem.tradeOrLegalName || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">{imsItem.month || '-'} {imsItem.year ? `- ${imsItem.year}` : ''}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">{imsItem.invoiceNumber || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">{imsItem.invoiceType || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-right text-gray-600">{typeof imsItem.invoiceValue === 'number' ? imsItem.invoiceValue.toFixed(2) : imsItem.invoiceValue || '0.00'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">{imsItem.placeOfSupply || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-right text-gray-600">{typeof imsItem.taxableValue === 'number' ? imsItem.taxableValue.toFixed(2) : imsItem.taxableValue || '0.00'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-right text-gray-600">{typeof imsItem.integratedTax === 'number' ? imsItem.integratedTax.toFixed(2) : imsItem.integratedTax || '0.00'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-right text-gray-600">{typeof imsItem.centralTax === 'number' ? imsItem.centralTax.toFixed(2) : imsItem.centralTax || '0.00'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-right text-gray-600">{typeof imsItem.stateTax === 'number' ? imsItem.stateTax.toFixed(2) : imsItem.stateTax || '0.00'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-right text-gray-600">{typeof imsItem.cess === 'number' ? imsItem.cess.toFixed(2) : imsItem.cess || '0.00'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">{imsItem.source || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">{imsItem.sourceReturnPeriod || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">{imsItem.sourceFilingStatus || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default IMSTable;